import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { v4 } from 'uuid';
import {
  IProduct,
  IOptionGroup,
  IOptionGroupItem,
  IProductOption,
  ISelectedOptionItem,
} from '../../common/interfaces';

export interface IProductOptionsResult {
  selectedOptions: string;
  selected_option_items: ISelectedOptionItem[];
  priceDelta: number;
  quantity: number;
}

interface ProductOptionsProps {
  product: IProduct;
  mode?: 'add' | 'edit';
  quantity?: number;
  initialSelections?: ISelectedOptionItem[];
  initialLegacy?: string;
  /** Devuelve un renglon por variante: cada combinacion distinta es una linea de venta. */
  onConfirm: (results: IProductOptionsResult[]) => void;
  onCancel: () => void;
}

/** Modelo unico para pintar tanto los grupos nuevos como las opciones viejas. */
interface UiOption {
  key: string;
  itemId: number | null;
  name: string;
  priceDelta: number;
}

interface UiGroup {
  key: string;
  groupId: number | null;
  name: string;
  legacy: boolean;
  required: boolean;
  min: number;
  max: number;
  options: UiOption[];
  defaultKey: string | null;
}

/**
 * Una variante es una combinacion completa de opciones con su cantidad: "2 sazonadas con
 * coca y enteras". Es el modelo, en vez de contar unidades por grupo, porque contar por
 * grupo no alcanza para el caso real: "de estas 5, la picosa la quiero en tiras y UNA de
 * las sazonadas picada para taco" no se puede expresar repartiendo sabor y corte por
 * separado — el sistema tendria que adivinar como se emparejan.
 */
interface Variant {
  uid: string;
  selection: Record<string, string[]>;
  quantity: number;
}

const ACCENT = '#4fc3f7';
const PANEL_BG = '#333';
const SURFACE_BG = '#2a2a2a';
const BORDER = '#555';
const WARN = '#ffb74d';
/** Minimo a granel: 50 gramos, igual que el prompt de cantidad del sidebar. */
const MIN_BULK_QTY = 0.05;

/**
 * Las opciones del catalogo viejo (COCCIÓN, CORTE, TIEMPO DE ESPERA) son obligatorias en el
 * POS: antes no se validaban y se podia cerrar la ventana sin contestar, lo que dejaba la
 * comanda incompleta. Afecta a los 60 productos que las tienen pegadas (42 parrilladas y el
 * resto cortes de carne).
 *
 * Es una politica del POS porque `products_options` no tiene columnas donde guardarla. Si
 * mañana alguna debe ser opcional o traer un valor por defecto, el camino es migrarla a un
 * grupo del motor nuevo (efecto 'none'), donde eso ya es configurable por producto.
 */
const LEGACY_OPTIONS_REQUIRED = true;

const effective = (pivotValue: any, groupValue: any) =>
  pivotValue !== null && pivotValue !== undefined ? pivotValue : groupValue;

/**
 * Firma de una seleccion ignorando un grupo. Con ella se sabe que variantes son "la misma
 * salvo por ese grupo", que es lo que hace posible repartir cantidades dentro de un grupo sin
 * perder lo que cada variante ya tenia contestado en los demas.
 */
const signatureExcept = (selection: Record<string, string[]>, exceptKey: string): string =>
  Object.keys(selection)
    .filter((key) => key !== exceptKey)
    .sort()
    .map((key) => `${key}:${[...(selection[key] ?? [])].sort().join('|')}`)
    .join(';');

/** Firma del contenido de una seleccion, para comparar variantes y juntar las iguales. */
const signatureOf = (selection: Record<string, string[]>): string =>
  Object.keys(selection)
    .sort()
    .map((key) => `${key}:${[...(selection[key] ?? [])].sort().join('|')}`)
    .join(';');

/**
 * Ventana de opciones del POS.
 *
 * Absorbe los dos catalogos a proposito: las parrilladas YA tienen pegadas las opciones
 * viejas (COCCIÓN, CORTE, TIEMPO DE ESPERA), asi que si esta ventana no las mostrara la
 * cajera recibiria dos ventanitas seguidas por cada parrillada.
 *
 * Es un Dialog de MUI y no un SweetAlert porque withReactContent renderiza un arbol de React
 * desprendido: el estado de adentro no participa del ciclo de render del padre, que es por lo
 * que el codigo viejo tiene que sacar las respuestas por una variable mutable de modulo y por
 * document.getElementById. Aqui hace falta validacion y precio en vivo.
 */
export const ProductOptions: React.FC<ProductOptionsProps> = ({
  product,
  mode = 'add',
  quantity: initialQuantity = 1,
  initialSelections = [],
  initialLegacy = '',
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const groups: UiGroup[] = useMemo(() => {
    const list: UiGroup[] = [];

    (product.option_groups ?? [])
      .slice()
      .sort((a, b) => (a.pivot?.sort_order ?? a.sort_order) - (b.pivot?.sort_order ?? b.sort_order))
      .forEach((group: IOptionGroup) => {
        const required = !!Number(effective(group.pivot?.is_required, group.is_required));
        const min = Number(effective(group.pivot?.min_select, group.min_select)) || 0;
        const max = Number(effective(group.pivot?.max_select, group.max_select)) || 1;
        const defaultItemId = group.pivot?.default_item_id ?? null;

        const options: UiOption[] = (group.items ?? [])
          .filter((item: IOptionGroupItem) => !!Number(item.is_active))
          .map((item: IOptionGroupItem) => ({
            key: `i-${item.id}`,
            itemId: item.id,
            name: item.name,
            priceDelta: Number(item.price_delta) || 0,
          }));

        const fallbackDefault = (group.items ?? []).find((item) => !!Number(item.is_default));

        list.push({
          key: `g-${group.id}`,
          groupId: group.id,
          name: group.name,
          legacy: false,
          required,
          min: required ? Math.max(min, 1) : min,
          max,
          options,
          defaultKey: defaultItemId
            ? `i-${defaultItemId}`
            : fallbackDefault
              ? `i-${fallbackDefault.id}`
              : null,
        });
      });

    // Opciones viejas: opcionales, sin precio y sin efecto en inventario.
    (product.options ?? []).forEach((option: IProductOption) => {
      let values: string[] = [];

      try {
        values = JSON.parse(option.sub_options) ?? [];
      } catch (e) {
        values = [];
      }

      const legacyOptions = values.map((value, index) => ({
        key: `l-${option.id}-${index}`,
        itemId: null,
        name: value,
        priceDelta: 0,
      }));

      list.push({
        key: `l-${option.id}`,
        groupId: null,
        name: option.name,
        legacy: true,
        required: LEGACY_OPTIONS_REQUIRED,
        min: LEGACY_OPTIONS_REQUIRED ? 1 : 0,
        max: 1,
        options: legacyOptions,
        // La tabla `options` no tiene donde guardar cual es el valor por defecto, asi que se
        // toma el primero de su lista. Con esto la ventana vuelve a resolverse con un Enter
        // en vez de exigir tres taps por parrillada.
        defaultKey: legacyOptions.length > 0 ? legacyOptions[0].key : null,
      });
    });

    return list.filter((group) => group.options.length > 0);
  }, [product]);
  // Los cortes que se venden por peso tambien pueden traer opciones (AGUJA NORTEÑA,
  // CHUCK SHORT RIBS). Antes el peso se pedia en una ventana aparte; aqui va en la misma.
  const isBulk = product.is_bulk === '0';
  const [weight, setWeight] = useState<string>(
    isBulk && Number(initialQuantity) > 0 ? String(initialQuantity) : ''
  );
  const weightValue = Number(weight);
  const weightIsValid = !isBulk || (weight !== '' && !isNaN(weightValue) && weightValue >= MIN_BULK_QTY);

  const [variants, setVariants] = useState<Variant[]>([]);
  const [activeUid, setActiveUid] = useState<string>('');
  const [attempted, setAttempted] = useState(false);

  // Llave PRIMITIVA del contenido de initialSelections. Es deliberada: la prop llega con
  // default `[]`, o sea un arreglo nuevo en cada render, asi que depender de su identidad
  // hacia que el efecto de abajo corriera en cada render, se llamara a setVariants, y el
  // ciclo volviera a empezar (bucle infinito que ademas borraba cada clic del usuario).
  const initialSelectionKey = (initialSelections ?? [])
    .map((selected) => selected.item_id)
    .sort((a, b) => a - b)
    .join(',');

  // Precarga: en modo edicion, lo que el renglon ya traia; si no, los valores por defecto,
  // de modo que el caso comun se resuelve con un solo Enter.
  useEffect(() => {
    const selection: Record<string, string[]> = {};
    const chosenIds = new Set(
      initialSelectionKey ? initialSelectionKey.split(',').map(Number) : []
    );
    const legacyPairs = new Map<string, string>();

    initialLegacy.split(',').forEach((fragment) => {
      const [name, value] = fragment.split(':');
      if (name && value) legacyPairs.set(name.trim().toUpperCase(), value.trim().toUpperCase());
    });

    groups.forEach((group) => {
      if (group.legacy) {
        const value = legacyPairs.get(group.name.toUpperCase());
        const match = value ? group.options.find((o) => o.name.toUpperCase() === value) : null;

        if (match) {
          selection[group.key] = [match.key];
          return;
        }

        // Sin valor previo se cae al default del grupo, igual que los grupos nuevos.
        selection[group.key] = mode === 'edit' || !group.defaultKey ? [] : [group.defaultKey];
        return;
      }

      const preselected = group.options.filter((o) => o.itemId !== null && chosenIds.has(o.itemId));

      if (preselected.length > 0) {
        selection[group.key] = preselected.map((o) => o.key);
        return;
      }

      selection[group.key] = mode === 'edit' || !group.defaultKey ? [] : [group.defaultKey];
    });

    const uid = v4();
    setVariants([
      { uid, selection, quantity: isBulk ? 1 : Math.max(1, Number(initialQuantity) || 1) },
    ]);
    setActiveUid(uid);
  }, [groups, initialSelectionKey, initialLegacy, mode, isBulk, initialQuantity]);

  const active = variants.find((variant) => variant.uid === activeUid) ?? variants[0] ?? null;

  const isSatisfied = useCallback(
    (group: UiGroup, selection: Record<string, string[]>) => {
      const count = (selection[group.key] ?? []).length;
      if (group.required) return count >= Math.max(group.min, 1);
      return count === 0 || count >= group.min;
    },
    []
  );

  const pendingFor = useCallback(
    (variant: Variant | null) =>
      variant ? groups.filter((group) => !isSatisfied(group, variant.selection)) : [],
    [groups, isSatisfied]
  );

  const pendingGroups = pendingFor(active);
  const incompleteVariants = variants.filter((variant) => pendingFor(variant).length > 0);
  const canConfirm = incompleteVariants.length === 0 && weightIsValid && variants.length > 0;

  const totalUnits = isBulk
    ? weightValue || 0
    : variants.reduce((sum, variant) => sum + variant.quantity, 0);

  // El teclado actua sobre el primer grupo sin resolver de la variante activa: la cajera
  // teclea 2, 1, 3 y avanza.
  const activeGroupKey = (pendingGroups[0] ?? groups[0])?.key ?? null;

  const updateActive = useCallback(
    (mutate: (variant: Variant) => Variant) => {
      setVariants((prev) =>
        prev.map((variant) => (variant.uid === activeUid ? mutate(variant) : variant))
      );
    },
    [activeUid]
  );

  const toggle = useCallback(
    (group: UiGroup, option: UiOption) => {
      updateActive((variant) => {
        const current = variant.selection[group.key] ?? [];
        const already = current.includes(option.key);
        let next: string[];

        if (group.max <= 1) {
          next = already && !group.required ? [] : [option.key];
        } else if (already) {
          next = current.filter((key) => key !== option.key);
        } else if (current.length >= group.max) {
          return variant;
        } else {
          next = [...current, option.key];
        }

        return { ...variant, selection: { ...variant.selection, [group.key]: next } };
      });
    },
    [updateActive]
  );

  /** Sube o baja unidades de una variante. Se usa el updater funcional para no perder toques
   *  que caigan en el mismo turno de JS: dos partirian del mismo valor y uno se perderia. */
  const bumpQuantity = useCallback((uid: string, delta: number) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.uid === uid
          ? { ...variant, quantity: Math.max(1, variant.quantity + delta) }
          : variant
      )
    );
  }, []);

  /**
   * Saca una unidad de la variante activa a una nueva, con las mismas opciones, para que la
   * cajera solo cambie lo que difiere. Si la activa solo tiene una, agrega una unidad nueva
   * en vez de vaciarla. El boton dice cual de las dos cosas va a hacer.
   */
  const splitActive = useCallback(() => {
    if (!active) return;

    const fresh: Variant = { uid: v4(), selection: { ...active.selection }, quantity: 1 };

    setVariants((prev) => {
      const index = prev.findIndex((variant) => variant.uid === active.uid);
      if (index === -1) return prev;

      const next = [...prev];
      if (next[index].quantity > 1) {
        next[index] = { ...next[index], quantity: next[index].quantity - 1 };
      }
      next.splice(index + 1, 0, fresh);
      return next;
    });

    setActiveUid(fresh.uid);
  }, [active]);

  /**
   * Las variantes que son iguales a la activa SALVO por este grupo. Es la "familia" del
   * grupo, y sobre ella se reparten las cantidades: asi se recupera el "cuantas de cada una"
   * sin perder lo que cada variante ya tiene contestado en los demas grupos.
   *
   * Es local a proposito. Si la cajera esta editando una variante de 1 pieza que difiere en
   * el sabor, el reparto del CORTE solo mueve esa pieza y no toca a las otras cuatro.
   */
  const familyOf = useCallback(
    (group: UiGroup): Variant[] => {
      if (!active) return [];
      const base = signatureExcept(active.selection, group.key);
      return variants.filter((variant) => signatureExcept(variant.selection, group.key) === base);
    },
    [active, variants]
  );

  const countsOf = useCallback(
    (group: UiGroup): Record<string, number> => {
      const counts: Record<string, number> = {};
      familyOf(group).forEach((variant) => {
        const key = (variant.selection[group.key] ?? [])[0];
        if (key) counts[key] = (counts[key] ?? 0) + variant.quantity;
      });
      return counts;
    },
    [familyOf]
  );

  /**
   * Mueve una pieza de una opcion a otra dentro del grupo. La variante origen baja una (y
   * desaparece si se queda en cero); la destino sube una, y se crea heredando TODO lo demas
   * de la seleccion actual si todavia no existe.
   */
  const moveUnit = useCallback(
    (group: UiGroup, fromKey: string, toKey: string) => {
      if (!active || fromKey === toKey) return;

      const base = signatureExcept(active.selection, group.key);
      const inFamily = (variant: Variant) =>
        signatureExcept(variant.selection, group.key) === base;

      const donor = variants.find(
        (variant) => inFamily(variant) && (variant.selection[group.key] ?? [])[0] === fromKey
      );
      if (!donor) return;

      const existing = variants.find(
        (variant) => inFamily(variant) && (variant.selection[group.key] ?? [])[0] === toKey
      );
      const targetUid = existing ? existing.uid : v4();

      // Si la pieza que se mueve era la ultima de la variante que se esta editando, esa
      // variante desaparece: el foco tiene que seguir a la nueva o la cajera pierde su lugar
      // y los siguientes toques terminan cambiando otro renglon.
      if (donor.uid === activeUid && donor.quantity <= 1) setActiveUid(targetUid);

      setVariants((prev) => {
        const donorIndex = prev.findIndex((variant) => variant.uid === donor.uid);
        if (donorIndex === -1) return prev;

        const next = [...prev];
        const targetIndex = next.findIndex((variant) => variant.uid === targetUid);

        if (targetIndex === -1) {
          next.splice(donorIndex + 1, 0, {
            uid: targetUid,
            selection: { ...donor.selection, [group.key]: [toKey] },
            quantity: 1,
          });
        } else {
          next[targetIndex] = { ...next[targetIndex], quantity: next[targetIndex].quantity + 1 };
        }

        const stillDonorIndex = next.findIndex((variant) => variant.uid === donor.uid);
        if (next[stillDonorIndex].quantity <= 1) next.splice(stillDonorIndex, 1);
        else next[stillDonorIndex] = { ...next[stillDonorIndex], quantity: next[stillDonorIndex].quantity - 1 };

        return next;
      });
    },
    [active, variants, activeUid]
  );

  /** El "+" saca la pieza de la variante que se esta editando; si esa YA es la opcion, se la
   *  quita a la hermana que mas lleve, para poder devolver piezas al monton. */
  const addUnitTo = useCallback(
    (group: UiGroup, option: UiOption) => {
      if (!active) return;
      const counts = countsOf(group);
      const activeKey = (active.selection[group.key] ?? [])[0];

      const from =
        activeKey && activeKey !== option.key
          ? activeKey
          : Object.entries(counts)
              .filter(([key, n]) => key !== option.key && n > 0)
              .sort((a, b) => b[1] - a[1])[0]?.[0];

      if (from) moveUnit(group, from, option.key);
    },
    [active, countsOf, moveUnit]
  );

  /** El "−" devuelve la pieza a la variante que se esta editando, o a la que mas lleve. */
  const removeUnitFrom = useCallback(
    (group: UiGroup, option: UiOption) => {
      if (!active) return;
      const counts = countsOf(group);
      const activeKey = (active.selection[group.key] ?? [])[0];

      const to =
        activeKey && activeKey !== option.key
          ? activeKey
          : Object.entries(counts)
              .filter(([key, n]) => key !== option.key && n > 0)
              .sort((a, b) => b[1] - a[1])[0]?.[0];

      if (to) moveUnit(group, option.key, to);
    },
    [active, countsOf, moveUnit]
  );

  const removeVariant = useCallback(
    (uid: string) => {
      setVariants((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((variant) => variant.uid !== uid);
        if (uid === activeUid) setActiveUid(next[0].uid);
        return next;
      });
    },
    [activeUid]
  );

  /** Arma UN renglon a partir de una seleccion. Formato de siempre para el ticket. */
  const buildLine = useCallback(
    (selection: Record<string, string[]>, lineQuantity: number): IProductOptionsResult => {
      const parts: string[] = [];
      const items: ISelectedOptionItem[] = [];
      let delta = 0;

      groups.forEach((group) => {
        const chosen = group.options.filter((option) =>
          (selection[group.key] ?? []).includes(option.key)
        );
        if (chosen.length === 0) return;

        parts.push(`${group.name.toUpperCase()}: ${chosen.map((o) => o.name.toUpperCase()).join('/')}`);

        chosen.forEach((option) => {
          delta += option.priceDelta;
          if (group.legacy || group.groupId === null || option.itemId === null) return;

          items.push({
            group_id: group.groupId as number,
            item_id: option.itemId,
            group_name: group.name,
            item_name: option.name,
            price_delta: option.priceDelta,
          });
        });
      });

      return {
        selectedOptions: parts.join(','),
        selected_option_items: items,
        priceDelta: delta,
        quantity: lineQuantity,
      };
    },
    [groups]
  );

  /** Un renglon por variante. Las que quedaron identicas tras editarlas se juntan. */
  const lines = useMemo(() => {
    const merged = new Map<string, { selection: Record<string, string[]>; quantity: number }>();

    variants.forEach((variant) => {
      const key = signatureOf(variant.selection);
      const found = merged.get(key);
      const quantity = isBulk ? weightValue : variant.quantity;
      if (found) found.quantity += quantity;
      else merged.set(key, { selection: variant.selection, quantity });
    });

    return Array.from(merged.values()).map((entry) => buildLine(entry.selection, entry.quantity));
  }, [variants, buildLine, isBulk, weightValue]);

  const basePrice = Number(product.price_1) || 0;
  const activeDelta = active ? buildLine(active.selection, 1).priceDelta : 0;
  const unitPrice = basePrice + activeDelta;
  const totalPrice = lines.reduce((sum, line) => sum + (basePrice + line.priceDelta) * line.quantity, 0);

  /**
   * Grupos que valen lo mismo en todas las variantes. Se muestran una sola vez abajo, y los
   * renglones de la izquierda solo pintan lo que DIFIERE: sin esto cada variante repetiria
   * los cinco grupos y la lista se vuelve ilegible.
   */
  const commonGroups = useMemo(
    () =>
      groups.filter((group) => {
        const first = [...(variants[0]?.selection[group.key] ?? [])].sort().join('|');
        return variants.every(
          (variant) => [...(variant.selection[group.key] ?? [])].sort().join('|') === first
        );
      }),
    [groups, variants]
  );

  const differingGroups = groups.filter((group) => !commonGroups.includes(group));

  const labelFor = useCallback(
    (variant: Variant, list: UiGroup[]) =>
      list
        .map((group) =>
          group.options
            .filter((option) => (variant.selection[group.key] ?? []).includes(option.key))
            .map((option) => option.name.toUpperCase())
            .join('/')
        )
        .filter(Boolean)
        .join(' · '),
    []
  );

  const commonSummary = active ? labelFor(active, commonGroups) : '';

  const handleConfirm = useCallback(() => {
    if (!canConfirm) {
      setAttempted(true);

      const broken = incompleteVariants[0];
      if (broken) {
        setActiveUid(broken.uid);
        const first = pendingFor(broken)[0];
        if (first) {
          document
            .getElementById(`option-group-${first.key}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    onConfirm(lines);
  }, [canConfirm, incompleteVariants, pendingFor, lines, onConfirm]);

  // Atajos de teclado: las cajeras son mucho mas rapidas tecleando que tocando.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleConfirm();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      const typingWeight = (event.target as HTMLElement)?.tagName === 'INPUT';

      if (/^[1-9]$/.test(event.key) && activeGroupKey && !typingWeight) {
        const group = groups.find((g) => g.key === activeGroupKey);
        const option = group?.options[Number(event.key) - 1];

        if (group && option) {
          event.preventDefault();
          // En modo contador teclear "1 1 2 3 3" va sumando piezas, que es la forma mas
          // rapida de capturar el reparto.
          const counts = countsOf(group);
          const familyUnits = Object.values(counts).reduce((sum, n) => sum + n, 0);
          if (!isBulk && group.max <= 1 && group.options.length > 1 && familyUnits > 1) {
            addUnitTo(group, option);
          } else {
            toggle(group, option);
          }
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleConfirm, onCancel, activeGroupKey, groups, toggle, countsOf, addUnitTo, isBulk]);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onCancel();
      }}
      PaperProps={{
        sx: {
          bgcolor: SURFACE_BG,
          color: '#fff',
          borderRadius: fullScreen ? 0 : 3,
          width: fullScreen ? '100%' : '96vw',
          maxWidth: fullScreen ? '100%' : 1500,
        },
      }}
    >
      {/* Encabezado: que producto es, cuanto va costando y que falta */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 26, fontWeight: 600, flex: 1, minWidth: 240 }}>
            {product.name}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            {activeDelta !== 0 && (
              <Typography component="span" sx={{ fontSize: 16, color: '#999', textDecoration: 'line-through', mr: 1 }}>
                ${basePrice.toFixed(2)}
              </Typography>
            )}
            <Typography component="span" sx={{ fontSize: 24, fontWeight: 700, color: ACCENT }}>
              ${unitPrice.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Cuanto se lleva el cliente. Es lo primero que se define. */}
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}
        >
          {isBulk ? (
            <>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '.5px' }}>
                PESO (KG)
              </Typography>
              <TextField
                size="small"
                autoFocus
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
                placeholder="0.000"
                error={attempted && !weightIsValid}
                helperText={attempted && !weightIsValid ? 'Mínimo 0.05 kg' : ''}
                sx={{
                  width: 140,
                  '& .MuiInputBase-input': { color: '#fff', fontSize: 24, fontWeight: 800, textAlign: 'center' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                }}
              />
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '.5px' }}>
                UNIDADES
              </Typography>
              <IconButton
                aria-label="bajar unidades"
                onClick={() => active && bumpQuantity(active.uid, -1)}
                disabled={!active || active.quantity <= 1}
                sx={{ color: '#ddd', border: `1px solid ${BORDER}`, width: 44, height: 44 }}
              >
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ minWidth: 52, textAlign: 'center', fontSize: 28, fontWeight: 800 }}>
                {totalUnits}
              </Typography>
              <IconButton
                aria-label="subir unidades"
                onClick={() => active && bumpQuantity(active.uid, 1)}
                sx={{ color: '#ddd', border: `1px solid ${BORDER}`, width: 44, height: 44 }}
              >
                <AddIcon />
              </IconButton>
              {variants.length > 1 && (
                <Typography sx={{ fontSize: 13, fontWeight: 600, ml: 1, color: '#81c784' }}>
                  {variants.length} variantes
                </Typography>
              )}
            </>
          )}
        </Box>

      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: fullScreen ? 'wrap' : 'nowrap' }}>
          {/* Columna de variantes: cada renglon es una combinacion completa. Tocarla la pone
              a editar; los grupos de la derecha siempre actuan sobre la que este activa. */}
          {!isBulk && (
            <Box sx={{ width: fullScreen ? '100%' : 250, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '.5px', mb: 1 }}>
                VARIANTES
              </Typography>

              {variants.map((variant, index) => {
                const isActive = variant.uid === active?.uid;
                const missing = pendingFor(variant).length > 0;
                const detail = labelFor(variant, differingGroups);

                return (
                  <Box
                    key={variant.uid}
                    role="button"
                    aria-label={`variante ${index + 1}`}
                    onClick={() => setActiveUid(variant.uid)}
                    sx={{
                      mb: 1,
                      p: 1.25,
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: isActive ? 'rgba(79,195,247,.14)' : PANEL_BG,
                      border: `2px solid ${missing && attempted ? WARN : isActive ? ACCENT : BORDER}`,
                      '&:hover': { borderColor: isActive ? ACCENT : '#777' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 15, color: '#d5d5d5', fontWeight: 700, mr: 0.5 }}>
                        {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={`bajar variante ${index + 1}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          bumpQuantity(variant.uid, -1);
                        }}
                        disabled={variant.quantity <= 1}
                        sx={{ color: '#ccc', border: `1px solid ${BORDER}`, p: 0.25,
                              '&.Mui-disabled': { color: '#555', borderColor: '#444' } }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ minWidth: 24, textAlign: 'center', fontSize: 19, fontWeight: 800, color: ACCENT }}>
                        {variant.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={`subir variante ${index + 1}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          bumpQuantity(variant.uid, 1);
                        }}
                        sx={{ color: '#ccc', border: `1px solid ${BORDER}`, p: 0.25 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>

                      {missing && (
                        <WarningAmberIcon sx={{ fontSize: 17, color: WARN, ml: 0.5 }} />
                      )}

                      {variants.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label={`quitar variante ${index + 1}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeVariant(variant.uid);
                          }}
                          sx={{ color: '#8a8a8a', ml: 'auto', p: 0.25, '&:hover': { color: '#e57373' } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: detail ? 700 : 500,
                        mt: 0.75,
                        lineHeight: 1.35,
                        color: missing ? WARN : detail ? '#fff' : '#d5d5d5',
                      }}
                    >
                      {detail ||
                        (missing
                          ? 'Falta contestar'
                          : variants.length === 1
                            ? 'Todas con las mismas opciones'
                            : 'Igual a las demás')}
                    </Typography>
                  </Box>
                );
              })}

              <Button
                fullWidth
                onClick={splitActive}
                startIcon={<CallSplitIcon sx={{ transform: 'rotate(90deg)' }} />}
                sx={{
                  minHeight: 44,
                  fontSize: 13,
                  fontWeight: 700,
                  color: ACCENT,
                  border: `2px dashed rgba(79,195,247,.5)`,
                  '&:hover': { bgcolor: 'rgba(79,195,247,.12)' },
                }}
              >
                {active && active.quantity > 1 ? 'Separar 1' : 'Otra igual'}
              </Button>
            </Box>
          )}

          {/* Grupos: siempre editan la variante activa */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 2,
            }}
          >
            {groups.map((group) => {
              const chosen = active?.selection[group.key] ?? [];
              const missing = attempted && active !== null && !isSatisfied(group, active.selection);
              const isActiveGroup = group.key === activeGroupKey;
              // "Cuantas de cada una" se enciende solo en cuanto hay mas de una pieza que
              // repartir; con una sola, la pregunta es cual y no cuantas.
              const counts = countsOf(group);
              const familyUnits = Object.values(counts).reduce((sum, n) => sum + n, 0);
              const asCounter = !isBulk && group.max <= 1 && group.options.length > 1 && familyUnits > 1;

              return (
                <Box
                  key={group.key}
                  id={`option-group-${group.key}`}
                  sx={{
                    bgcolor: PANEL_BG,
                    borderRadius: 2,
                    p: 2,
                    border: `1px solid ${missing ? WARN : isActiveGroup ? 'rgba(79,195,247,.5)' : BORDER}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '.5px' }}>
                      {group.name.toUpperCase()}
                    </Typography>
                    <Chip
                      size="small"
                      label={group.required ? 'Obligatorio' : 'Opcional'}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        bgcolor: group.required ? 'rgba(239,83,80,.18)' : 'rgba(255,255,255,.08)',
                        color: group.required ? '#ef9a9a' : '#bbb',
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: 14, color: asCounter ? ACCENT : '#d5d5d5', mb: 1.5 }}>
                    {asCounter
                      ? `Cuántas de cada una · ${familyUnits} piezas`
                      : group.max > 1
                        ? `Elige hasta ${group.max}`
                        : 'Elige 1'}
                    {isActiveGroup && ' · usa las teclas numéricas'}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {group.options.map((option, index) => {
                      const units = counts[option.key] ?? 0;
                      const selected = asCounter ? units > 0 : chosen.includes(option.key);
                      const blocked = !asCounter && !selected && group.max > 1 && chosen.length >= group.max;

                      return (
                        <Box
                          key={option.key}
                          onClick={() =>
                            asCounter ? addUnitTo(group, option) : !blocked && toggle(group, option)
                          }
                          sx={{
                            position: 'relative',
                            flex: '1 1 130px',
                            minWidth: 130,
                            minHeight: asCounter ? 100 : 84,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            px: 1.5,
                            borderRadius: 2.5,
                            cursor: blocked ? 'not-allowed' : 'pointer',
                            opacity: blocked ? 0.4 : 1,
                            bgcolor: selected ? 'rgba(79,195,247,.14)' : '#3a3a3a',
                            border: `2px solid ${selected ? ACCENT : BORDER}`,
                            transition: 'background-color .12s, border-color .12s',
                            '&:hover': blocked ? {} : { borderColor: selected ? ACCENT : '#777' },
                          }}
                        >
                          {isActiveGroup && index < 9 && (
                            <Typography
                              sx={{ position: 'absolute', top: 5, left: 8, fontSize: 11, color: '#8a8a8a', fontWeight: 700 }}
                            >
                              {index + 1}
                            </Typography>
                          )}

                          {selected && !asCounter && (
                            <CheckCircleIcon sx={{ position: 'absolute', top: 5, right: 6, fontSize: 18, color: ACCENT }} />
                          )}

                          {asCounter ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%' }}>
                              <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
                                {option.name.toUpperCase()}
                              </Typography>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <IconButton
                                  size="small"
                                  aria-label={`quitar ${option.name}`}
                                  onClick={() => removeUnitFrom(group, option)}
                                  disabled={units === 0 || units >= familyUnits}
                                  sx={{
                                    color: '#ccc',
                                    border: `1px solid ${BORDER}`,
                                    p: 0.25,
                                    '&.Mui-disabled': { color: '#555', borderColor: '#444' },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                  sx={{ minWidth: 26, textAlign: 'center', fontSize: 22, fontWeight: 800, color: units > 0 ? ACCENT : '#777' }}
                                >
                                  {units}
                                </Typography>
                                <IconButton
                                  size="small"
                                  aria-label={`agregar ${option.name}`}
                                  onClick={() => addUnitTo(group, option)}
                                  disabled={units >= familyUnits}
                                  sx={{
                                    color: '#ccc',
                                    border: `1px solid ${BORDER}`,
                                    p: 0.25,
                                    '&.Mui-disabled': { color: '#555', borderColor: '#444' },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 17, fontWeight: 600, lineHeight: 1.2 }}>
                              {option.name.toUpperCase()}
                            </Typography>
                          )}

                          {option.priceDelta !== 0 && (
                            <Chip
                              size="small"
                              label={`${option.priceDelta > 0 ? '+' : '−'}$${Math.abs(option.priceDelta).toFixed(2)}`}
                              sx={{
                                position: 'absolute',
                                bottom: 6,
                                right: 6,
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: option.priceDelta > 0 ? 'rgba(255,183,77,.2)' : 'rgba(129,199,132,.2)',
                                color: option.priceDelta > 0 ? WARN : '#81c784',
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* Lo que comparten TODAS las variantes se dice una sola vez: es lo que evita que el
            pie repita los cinco grupos por cada combinacion y termine haciendo scroll. */}
        <Box sx={{ flex: 1, minWidth: 260 }}>
          {canConfirm ? (
            <Typography sx={{ fontSize: 15, color: '#b2e07a', lineHeight: 1.5 }}>
              {commonSummary ? `Iguales en todas: ${commonSummary}` : 'Sin opciones seleccionadas'}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 15, color: WARN, lineHeight: 1.5 }}>
              {pendingGroups.length > 0
                ? `Falta elegir: ${pendingGroups.map((g) => g.name).join(', ')}`
                : incompleteVariants.length > 0
                  ? `Falta contestar en ${incompleteVariants.length} variante(s)`
                  : 'Capture el peso (mínimo 0.05 kg)'}
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ minHeight: 56, minWidth: 130, color: '#ccc', borderColor: BORDER, fontSize: 16 }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{
            minHeight: 56,
            minWidth: 220,
            fontSize: 17,
            fontWeight: 700,
            bgcolor: ACCENT,
            color: '#06232e',
            '&:hover': { bgcolor: '#29b6f6' },
          }}
        >
          {mode === 'edit'
            ? `Guardar ${isBulk ? '' : totalUnits} · $${totalPrice.toFixed(2)}`
            : `Agregar ${isBulk ? '' : totalUnits} · $${totalPrice.toFixed(2)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
